/**
 * Utility functions used across the app.
 */
import { THEMES, PLAN_FEATURES } from "./constants";
import hu from "./i18n/hu";
import en from "./i18n/en";
import { localStore } from "./utils/safeStorage";

// Week / month keys of a stored "YYYY-MM-DD" date. They live in `@sendly/shared`
// because the TV board's Cloud Function windows ascents with the very same
// functions; re-exported here so the existing importers stay as they are.
export { getWeekStart, getMonthKey } from "@sendly/shared";
// The gym slug rule lives in `@sendly/shared` (the MCP `create_gym` uses it too).
export { generateSlug } from "@sendly/shared";

/**
 * Chart bucket keys from a Date's LOCAL components. `toISOString()` would
 * shift a bucket into the previous month for any timezone east of UTC (May 1
 * 00:00 in Budapest is April 30 22:00 UTC), so a creation in the current month
 * silently vanished from the chart. Three charts had these two helpers inline.
 */
export function localMonthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
export function localDayKey(d) {
  return `${localMonthKey(d)}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * The trailing `count` calendar months ending this month, oldest first, as
 * `{ key: "YYYY-MM", label: "Sep '26" }` — the x-axis every monthly chart
 * shares, so their columns line up and their labels read the same.
 */
export function buildMonthAxis(count, lang, now = new Date()) {
  const out = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: localMonthKey(d), label: d.toLocaleDateString(resolveLocale(lang), { month: "short", year: "2-digit" }) });
  }
  return out;
}

/** The trailing `count` days ending today, oldest first, as `{ key: "YYYY-MM-DD" }`. */
export function buildDayAxis(count, now = new Date()) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const out = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push({ key: localDayKey(d) });
  }
  return out;
}

/** Get 1-2 letter uppercase initials from a name string. */
export function initials(n) {
  return n ? n.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) : "?";
}

/** Non-breaking hyphen (U+2011) — renders identically to "-" but never wraps. */
const NB_HYPHEN = "‑";

/**
 * Display name for a container that may wrap it onto several lines (the
 * `ProfileMenu` chip). A name must break at a SPACE, never mid-word —
 * "Elszánt Béta-mester" has to read "Elszánt" / "Béta-mester", never
 * "Elszánt Béta-" / "mester".
 *
 * CSS alone cannot express that: an ASCII hyphen is a soft wrap opportunity,
 * and `word-break: keep-all` does NOT suppress it in Chrome (verified — it
 * only governs CJK). The one thing that does is swapping the character for a
 * non-breaking hyphen, so the hyphenated word stays atomic and the space is
 * the only break the line-breaker can take.
 *
 * Display only — the chip's `aria-label`, the menu and every stored value keep
 * the original string.
 */
export function wrappableName(n) {
  return n ? n.replace(/-/g, NB_HYPHEN) : "";
}

/**
 * uid → profile photo, for the avatar on a `UserRow`. Returns "" when there is
 * none, so callers can pass the result straight through without a guard.
 *
 * The public profile projection is the only place a photo lives — a `members`
 * doc has no `photoURL`, and a guest competitor has no account at all, so both
 * fall through to the monogram circle. A user who picked the monogram avatar in
 * Settings already has `photoURL: null` on the doc, so no preference check is
 * needed here.
 */
export function buildPhotoResolver(users) {
  const map = {};
  (users || []).forEach(u => { if (u.uid && u.photoURL) map[u.uid] = u.photoURL; });
  return (uid) => map[uid] || "";
}

/**
 * uid → contact e-mail, for the second line of a `UserRow` and for roster
 * search haystacks. Reads the `members` list, which carries the address merged
 * in from the gym-admin-readable `memberContacts` subcollection — the public
 * profile projection deliberately has none. Returns "" when unknown, so it can
 * be folded into a search string unconditionally.
 *
 * Only the PRIMARY address is denormalized onto memberContacts; a complete
 * address that matches nothing locally is resolved server-side by
 * `useGymMemberEmailLookup`.
 */
export function buildEmailResolver(members) {
  const map = {};
  (members || []).forEach(m => { if (m.uid && m.email) map[m.uid] = m.email; });
  return (uid) => map[uid] || "";
}

/**
 * Effective avatar photo URL for the signed-in user's OWN avatar.
 * Returns null when the user chose the monogram avatar in Settings
 * (users/{uid}.avatarPreference === "monogram"), so callers fall back to
 * the initials circle. Other viewers don't need this helper — they read
 * users/{uid}.photoURL, which is nulled while the monogram preference is on.
 *
 * The auth token keeps the provider photo even in monogram mode (we only null
 * the Firestore doc), so on a cold boot / route switch — when `authUser` is
 * ready but `userData` hasn't resolved yet — we consult the synchronous
 * `sendly-user-name` boot cache to avoid flashing the provider photo before
 * the monogram preference loads.
 */
export function avatarPhotoURL(authUser, userData) {
  if (userData) {
    return userData.avatarPreference === "monogram"
      ? null
      : (userData.photoURL || authUser?.photoURL || null);
  }
  // Doc not loaded yet — fall back to the boot cache for the preference.
  const c = localStore.getJSON("sendly-user-name");
  if (c && authUser && c.uid === authUser.uid && c.avatarPreference === "monogram") return null;
  return authUser?.photoURL || null;
}

/** Today's date as "YYYY-MM-DD". Accepts optional IANA timezone (e.g. "Europe/Budapest"). */
export function todayStr(timezone) {
  // Browser local time (better than UTC for most users). Also the safety net
  // when the timezone path can't produce a clean ISO date.
  const local = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  if (timezone) {
    // Assemble the ISO date from structured parts, NOT a locale-formatted
    // string. Some browsers render a bare `en-CA` date as US-style "M/D/YYYY"
    // (observed in production: ascent.date "7/3/2026" instead of "2026-07-03"),
    // which sorts outside every ISO window — vanishing from the leaderboard
    // "today" filter and the challenge range query — and crashes stats memos on
    // `new Date(date).toISOString()`. formatToParts returns the numeric
    // year/month/day regardless of the locale's ordering, so the YYYY-MM-DD we
    // build is guaranteed well-formed. Falls back to browser-local on an invalid
    // timezone (Intl throws) or if any part is missing.
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
      }).formatToParts(new Date());
      const pick = (type) => parts.find(p => p.type === type)?.value;
      const s = `${pick('year')}-${pick('month')}-${pick('day')}`;
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    } catch { /* invalid timezone — use local fallback below */ }
  }
  return local();
}

/**
 * Parse a "YYYY-MM-DD" date string into a Date object at noon UTC.
 * Using noon avoids DST boundary issues when doing date arithmetic.
 * @param {string} dateStr - "YYYY-MM-DD"
 * @returns {Date}
 */
export function parseUTCDate(dateStr) {
  return new Date(dateStr + "T12:00:00Z");
}

/**
 * Format a Date object as "YYYY-MM-DD" (UTC-safe).
 * @param {Date} d
 * @returns {string}
 */
export function formatDateKey(d) {
  return d.toISOString().split("T")[0];
}

/**
 * Convert any common date representation to a Date. Returns null on
 * unparseable input (rather than the JS quirk of `new Date("garbage")`
 * giving an Invalid Date object that throws downstream).
 *
 *   - Date instance → returned as-is
 *   - Firestore Timestamp (has `.toDate()`) → unwrapped
 *   - Number → treated as epoch ms
 *   - "YYYY-MM-DD" string → parsed as LOCAL midnight (most ascent /
 *     event dates are stored as local-day strings; UTC parse would
 *     shift display by one day in TZ east of UTC)
 *   - Other string → passed to `new Date(...)`
 */
function toDate(v) {
  if (v == null || v === "") return null;
  try {
    if (v instanceof Date) return v;
    if (typeof v === "object" && typeof v.toDate === "function") return v.toDate();
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(v + "T00:00:00");
      return new Date(v);
    }
  } catch { return null; }
  return null;
}

/**
 * Resolve the BCP-47 locale tag for the app's currently selected
 * language. The whole codebase formats dates against the user's
 * chosen UI language — never the OS default — so that two users
 * sharing one device but different language settings see dates the
 * same way the rest of the UI is rendered.
 *
 * **`en-GB`, so English dates read 25/08/2026.** The audience is Hungarian
 * gyms and their mostly-European visitors, for whom `8/25/2026` is ambiguous
 * at a glance. Twenty formatters used to inline `en-US` and seven `en-GB`,
 * with no rule separating them — the majority was an accident, not a decision.
 * Every user-facing formatter now calls this; change the tag here and they all
 * move.
 *
 * NOT every locale literal in the codebase is a display decision. Three are
 * deliberately left alone and say so at their own site: the timezone parse in
 * `getHourInTimezone` (a machine format whose short weekday names feed a
 * lookup table), the USD figure in PlatformRevenueChart, and the speech
 * accent in CompetitionCountdown.
 */
export function resolveLocale(lang) {
  return lang === "hu" ? "hu-HU" : "en-GB";
}

/**
 * Format a date for display in the user's locale (date only, no time).
 * Uses the app's selected language for the locale tag — HU → Hungarian
 * dotted format ("2026. 05. 11."), EN → British slashed format
 * ("11/05/2026", see `resolveLocale`). Returns "" for unparseable input.
 *
 * Use this everywhere a human-readable calendar day is shown (member-
 * since, ascent date column, event date, comp date, audit log date
 * cells). Don't reach for `toLocaleDateString()` inline — the call is
 * easy to get wrong in subtle ways (timezone shifts on "YYYY-MM-DD"
 * inputs, NaN propagation from invalid dates, OS-locale leaking
 * through when no locale is passed, inconsistent output across
 * files), and inline calls drift from each other over time.
 *
 * @param {*} v - Date, Firestore Timestamp, ISO string, "YYYY-MM-DD", or epoch ms
 * @param {string} [lang] - app language code ("hu" / "en"); falls back to "en"
 * @param {Intl.DateTimeFormatOptions} [options] - optional override (e.g. { month: "short", day: "numeric" })
 */
export function formatDate(v, lang, options) {
  const d = toDate(v);
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleDateString(resolveLocale(lang), options);
}

/**
 * Format a start–end date span for display — a competition's or event's run.
 *
 * Collapses to a single date when there is no end, or when the two are the
 * same day; a one-day competition should not read "22 Aug – 22 Aug".
 *
 * **Use this instead of interpolating the raw stored strings.** Competition
 * dates are stored as `YYYY-MM-DD`, which renders as a perfectly readable
 * string — so twelve surfaces printed `{comp.startDate} — {comp.endDate}`
 * straight into the page and nobody noticed they were showing ISO to a human,
 * in every language, including the public spectator dashboard and the TV wall.
 * The separator is an EN DASH, as everywhere else a range is written.
 *
 * @param {*} start - Date / Timestamp / "YYYY-MM-DD" / ISO string / epoch ms
 * @param {*} end   - same; falsy or equal to `start` collapses the span
 * @param {string} [lang] - app language code ("hu" / "en")
 * @param {Intl.DateTimeFormatOptions} [options] - defaults to a short,
 *   year-bearing form ("22 Aug 2026" / "2026. aug. 22.") — competitions are
 *   listed across years, so dropping the year makes a past season ambiguous
 */
export function formatDateSpan(start, end, lang, options = { year: "numeric", month: "short", day: "numeric" }) {
  const s = formatDate(start, lang, options);
  if (!s) return "";
  const e = end ? formatDate(end, lang, options) : "";
  return !e || e === s ? s : `${s} – ${e}`;
}

/**
 * Format a date + time for display in the user's locale. Same locale
 * rules as `formatDate`. Use this for audit log timestamps,
 * last-active markers, comp clock instants, etc.
 *
 * @param {*} v - Date / Timestamp / ISO string / epoch ms
 * @param {string} [lang] - app language code ("hu" / "en")
 * @param {Intl.DateTimeFormatOptions} [options]
 */
export function formatDateTime(v, lang, options) {
  const d = toDate(v);
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleString(resolveLocale(lang), options);
}

/** Resolve a theme palette by name ("light" or "dark"). */
export function getTheme(mode) { return THEMES[mode] || THEMES.light; }

/** Generate accent-colored CSS variables for button styling. */
export function makeB(accent) {
  return {
    bg: accent,
    hover: accent + "dd",
    ring: accent + "33",
    danger: "#dc2626",
    dangerHover: "#b91c1c",
    dangerRing: "#dc262633",
  };
}

/**
 * Get normalized maps array from settings.
 * Backward compatible: if no maps[] array, creates one from legacy single-map fields.
 */
export function getMaps(settings, t) {
  if (settings.maps && settings.maps.length > 0) return settings.maps;
  return [{
    id: "default",
    name: t ? t("map.defaultName") : "Térkép",
    imageUrl: settings.mapImageUrl || "",
    width: settings.mapWidth || 520,
    height: settings.mapHeight || 480,
  }];
}

/**
 * Cache-busting version token for a gym map image.
 *
 * The same-origin proxy URL (`/manifest/{slug}/map/{mapId}.webp`) is
 * keyed only on slug + mapId and is cached 24h at BOTH the browser and
 * the CDN. Without a version query the browser/CDN keep serving the
 * PREVIOUS image for up to a day after an admin re-uploads — the map
 * "won't refresh no matter what". This token changes on every upload so
 * the proxy URL resolves to a fresh cache entry immediately.
 *
 * Prefers the explicit `imageUpdatedAt` stamp written at upload time;
 * falls back to the Firebase Storage download token embedded in
 * `imageUrl` (Storage rotates it on every overwrite) so maps uploaded
 * before the stamp existed still bust correctly.
 */
export function mapImageVersion(map) {
  if (!map) return null;
  if (map.imageUpdatedAt) return String(map.imageUpdatedAt);
  const tokenMatch = (map.imageUrl || "").match(/[?&]token=([^&]+)/);
  return tokenMatch ? tokenMatch[1] : null;
}

/**
 * Return a same-origin proxy URL for a gym map image, falling back to
 * the raw Storage URL when proxy inputs are missing (no slug, no mapId).
 *
 * Why: cross-origin Firebase Storage URLs bypass the SW + Hosting CDN —
 * every load is a fresh GET to Storage. Constrained devices (Samsung
 * Tizen TV browser) evict from their tiny HTTP cache between TV-slide
 * rotations, so the map "loads slowly every time, not just the first."
 * Routing through `/manifest/{slug}/map/{mapId}.webp` (handled by the
 * `manifest` CF) flips the request to same-origin so the browser cache
 * + Hosting CDN can hold it.
 *
 * A `?v=` cache-buster (see mapImageVersion) is appended so a re-upload
 * is picked up immediately instead of after the proxy's 24h cache TTL.
 * The `manifest` CF matches on `req.path` only, so the query is ignored
 * server-side but still keys the browser + CDN cache.
 *
 * Safe to call with partial data — returns the original imageUrl
 * unchanged when slug/mapId/imageUrl is missing, so callers can use it
 * everywhere without an explicit fallback.
 */
export function resolveMapImageUrl(map, gymSlug) {
  if (!map || !map.imageUrl) return "";
  if (!gymSlug || !map.id) return map.imageUrl;
  // In dev (`npm start`) the Firebase Hosting rewrite isn't running, so
  // `/manifest/**` would either 404 or hit the Vite dev server's SPA
  // fallback (serving index.html instead of bytes). The proxy is only useful
  // in production builds anyway — CDN + same-origin caching are what the
  // proxy is buying us. Vite replaces `import.meta.env.PROD` at build time,
  // so this branch is dead-code-eliminated in the prod bundle.
  if (!import.meta.env.PROD) return map.imageUrl;
  const base = "/manifest/" + encodeURIComponent(gymSlug) + "/map/" + encodeURIComponent(map.id) + ".webp";
  const ver = mapImageVersion(map);
  return ver ? base + "?v=" + encodeURIComponent(ver) : base;
}

// Merge custom difficulty/holdColors from settings with defaults
export function getDifficulty(settings, defaults) {
  // The seeded palette's `tc` values are hand-authored and correct, and the
  // identity of this object is a memo dependency in several consumers — so the
  // uncustomised path keeps returning the very same object.
  if (settings.customDifficulty == null) return defaults;
  const custom = settings.customDifficulty;
  const result = {};
  let i = 0;
  for (const [k, v] of Object.entries(custom)) {
    // `tc` is DERIVED, never trusted from storage: the level editor writes
    // `color`/`border` from one picker and leaves the stored `tc` behind, so a
    // recoloured level carried the previous colour's ink. See `inkOnFill`.
    result[k] = { ...v, tc: inkOnFill(v?.color), order: v.order ?? (defaults[k]?.order ?? i) };
    i++;
  }
  return result;
}
export function getHoldColors(settings, defaults) {
  if (settings.customHoldColors == null) return defaults;
  const custom = settings.customHoldColors;
  const result = {};
  let i = 0;
  for (const [k, v] of Object.entries(custom)) {
    result[k] = { ...v, order: v.order ?? (defaults[k]?.order ?? i) };
    i++;
  }
  return result;
}

/**
 * Firestore caps every `in` / `documentId() in` / `array-contains-any`
 * query at 30 values (raised from 10 in 2023). Hard-coding the number at the
 * call site is how the public competition dashboard ended up issuing 3x the
 * reads of the modal next to it — so the number lives here, once.
 */
export const IN_QUERY_LIMIT = 30;

/**
 * Split ids into Firestore-sized batches for an `in` query.
 * Drops falsy entries and de-duplicates, because both cost a wasted slot in a
 * capped batch. Returns [] for an empty input, so the caller's loop is a no-op
 * instead of a query with an empty (illegal) `in` array.
 */
export function chunkForInQuery(ids, size = IN_QUERY_LIMIT) {
  const unique = [...new Set((Array.isArray(ids) ? ids : []).filter(Boolean))];
  const out = [];
  for (let i = 0; i < unique.length; i += size) out.push(unique.slice(i, i + size));
  return out;
}

/**
 * Coerce whatever a timestamp arrives as into epoch milliseconds.
 *
 * Firestore hands back a `Timestamp` from a live listener but a plain
 * `{seconds, nanoseconds}` from a REST/cached read, a `Date` once anything has
 * round-tripped through JSON revival, and an ISO string or raw number where a
 * form wrote it. Three files each grew their own version of this ladder, and
 * they were the same ladder in a different order.
 *
 * Returns `null` rather than `0` or `NaN` for anything it cannot place, so a
 * caller has to decide what a missing timestamp means instead of silently
 * getting 1970. Sort comparators that want `0` say `toMs(x) ?? 0`.
 */
export function toMs(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? value : null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const n = new Date(value).getTime();
  return Number.isFinite(n) ? n : null;
}

/**
 * The `"<scope>:<key>"` shape the persisted-UI-state hooks index their blobs by,
 * so the same control on two gyms remembers two values. A null key means "no
 * persistence" and travels as null all the way down, which is what lets those
 * hooks degrade to plain `useState`.
 */
export function scopedKey(key, scope = null) {
  return key ? (scope ? `${scope}:${key}` : key) : null;
}

/**
 * A single localStorage entry holding one JSON object, read and written whole.
 *
 * Three hooks (`useExpanderState`, `usePersistedPeriod`, `useScrollMemory`) keep
 * their state this way, and each had written the same try/catch pair. Both
 * halves swallow: storage throws in a private window and on a browser set to
 * block site data, and a half-written blob must not take a render down with it —
 * losing a remembered scroll offset is not worth an error screen.
 */
export function makeKeyedStore(storageKey) {
  return {
    read() {
      return localStore.getJSON(storageKey, {});
    },
    write(store) {
      localStore.setJSON(storageKey, store);
    },
  };
}

// Sort object entries by order field
export function sortedEntries(obj) {
  return Object.entries(obj).sort((a, b) => (a[1].order ?? 999) - (b[1].order ?? 999));
}

/**
 * Difficulty entries hardest first — the one order of every grade chart and
 * breakdown row list (pyramid, flash rate, grade distribution, rating by
 * difficulty…): the grade pyramid's wide easy base sits at the bottom. Exactly
 * the reverse of `sortedEntries`, so a level without `order` comes first here.
 * Pickers, filters, legends and index ladders keep `sortedEntries`; the
 * `gradeChartOrder` gate holds the register of those.
 */
export function sortedEntriesHardestFirst(obj) {
  return sortedEntries(obj).reverse();
}

/** Perceived luminance of a hex color (0 = black, 1 = white). */
export function getLuminance(hex) {
  if (!hex) return 0;
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/** WCAG relative luminance of a `#rrggbb` (gamma-corrected, unlike getLuminance). */
function srgbLuminance(hex) {
  const c = String(hex || "").replace("#", "");
  if (c.length !== 6) return 0;
  const ch = (i) => {
    const v = parseInt(c.substr(i, 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4);
}

/** WCAG contrast ratio between two `#rrggbb` colors (1 = identical, 21 = max). */
export function contrastRatio(a, b) {
  const la = srgbLuminance(a);
  const lb = srgbLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Blend a `#rrggbb` toward a single channel target (0 = black, 255 = white). */
function mixToward(hex, target, amount) {
  const c = String(hex || "").replace("#", "");
  if (c.length !== 6) return hex;
  const ch = (i) => {
    const v = parseInt(c.substr(i, 2), 16);
    return Math.round(v + (target - v) * amount).toString(16).padStart(2, "0");
  };
  return `#${ch(0)}${ch(2)}${ch(4)}`;
}

/**
 * Flatten `top` at `alpha` over `bottom` into a solid `#rrggbb`.
 *
 * Needed wherever the *resulting* surface color has to be known — picking a
 * legible ink for a tinted pill, for instance. An `rgba()` background looks
 * the same but can't be measured.
 */
export function blendHex(top, bottom, alpha) {
  const a = String(top || "").replace("#", "");
  const b = String(bottom || "").replace("#", "");
  if (a.length !== 6 || b.length !== 6) return bottom;
  const k = Math.min(1, Math.max(0, alpha));
  const ch = (i) => {
    const va = parseInt(a.substr(i, 2), 16);
    const vb = parseInt(b.substr(i, 2), 16);
    return Math.round(vb + (va - vb) * k).toString(16).padStart(2, "0");
  };
  return `#${ch(0)}${ch(2)}${ch(4)}`;
}

/**
 * Nudge a color until it is legible AS TEXT on `bg`.
 *
 * A gym accent is picked to work as a chip *fill* behind white text, which
 * says nothing about how it reads as ink. Outline chips (transparent
 * background, colored border + label) use it the other way round, and a dark
 * accent like #376f8b lands at ~3.4:1 on the dark theme's #111 — under the
 * 4.5:1 needed at 11px. Lift toward white on dark surfaces, toward black on
 * light ones, in small steps, and give up rather than loop.
 *
 * @param {string} hex   the source color ("#rrggbb")
 * @param {string} bg    the surface it will sit on ("#rrggbb")
 * @param {number} [min] target contrast ratio (default 4.5)
 */
export function readableInk(hex, bg, min = 4.5) {
  if (!/^#[0-9a-f]{6}$/i.test(String(hex || "")) || !/^#[0-9a-f]{6}$/i.test(String(bg || ""))) return hex;
  const target = srgbLuminance(bg) < 0.2 ? 255 : 0;
  let out = hex;
  for (let i = 0; i < 12 && contrastRatio(out, bg) < min; i++) {
    out = mixToward(out, target, 0.12);
  }
  return out;
}

/**
 * Legible chip colors for ANY gym-picked color on a themed surface — group,
 * difficulty level, hold color.
 *
 * A group color is picked in a color input that knows nothing about the
 * viewer's theme, so both ends of the range collapse into the page: a black
 * group disappears on the dark theme's `#1e1e1e` card, a white one on the
 * light theme's card. Only the FILL keeps the raw value — a black group has to
 * read as black, lightening it to grey would make it a different group at a
 * glance. Everything DERIVED from it (label ink, border, swatch ring) goes
 * through `readableInk`, which returns a color untouched when it already has
 * contrast and lifts it only when it does not.
 *
 * Ink and ring are lifted to DIFFERENT thresholds on purpose. `ink` carries
 * 12px text, so it owes the 4.5:1 of WCAG AA; `ring` only has to make a shape
 * discernible, which is the 3:1 of non-text contrast. Holding the ring to the
 * text threshold would repaint every mid-tone group: `#e11d48` measures
 * 3.25:1 on the dark theme — plainly visible as a dot, but it would have been
 * washed out to `#eb6582` to satisfy a rule about reading.
 *
 * @param {string} color   the group's picked color ("#rrggbb")
 * @param {string} bg      the surface the chip sits on ("#rrggbb")
 * @param {number} [alpha] tint strength of the chip fill over `bg`
 * @returns {{fill:string, surface:string, ink:string, ring:string, border:string}}
 */
export function groupTone(color, bg, alpha = 0.13) {
  const fill = /^#[0-9a-f]{6}$/i.test(String(color || "")) ? color : "#cccccc";
  const surface = blendHex(fill, bg, alpha);
  const ring = readableInk(fill, surface, 3);
  return { fill, surface, ink: readableInk(fill, surface), ring, border: ring + "66" };
}

/**
 * Text colour that reads ON a solid fill — white for a dark fill, near-black
 * for a light one.
 *
 * This is the INVERSE of `readableInk`: that one adjusts a colour so it can be
 * read on a surface, this one picks an ink for a surface made of that colour.
 *
 * Difficulty levels used to STORE this as a `tc` field, which the level editor
 * never updated — it writes `color` and `border` from the one picker and
 * leaves `tc` at whatever the seed put there. So recolouring the white level
 * (seeded `tc: "#333"`) to a dark navy left `#333` text on it, at 1.22:1, in
 * BOTH themes. A stored derived value is a stored bug waiting for someone to
 * edit around it; derived on read it cannot go stale.
 *
 * The 0.5 cut is on PERCEIVED luminance, not the gamma-corrected WCAG one, and
 * it is chosen to reproduce all six seeded `tc` values exactly (any threshold
 * in 0.434–0.690 does), so no gym's palette shifts under it. Picking by raw
 * contrast ratio instead would flip green to dark text — measurably better
 * (3.83 vs 3.30) but still under AA either way, and a visible change to a
 * palette nobody asked to restyle.
 */
export function inkOnFill(fill) {
  if (!/^#[0-9a-f]{6}$/i.test(String(fill || ""))) return "#ffffff";
  return getLuminance(fill) < 0.5 ? "#ffffff" : "#333333";
}

/**
 * Re-derive every difficulty level's `tc` from its own fill.
 *
 * Difficulty config reaches components through TWO resolvers — `getDifficulty`
 * for single-discipline gyms and `getDifficultyForDiscipline` for mixed ones —
 * and eighteen render sites read `tc` off whatever those hand back. Both go
 * through here so a mixed gym cannot end up with the stale ink a plain gym no
 * longer has.
 */
export function withDerivedInk(levels) {
  if (!levels) return levels;
  const out = {};
  for (const [k, v] of Object.entries(levels)) out[k] = { ...v, tc: inkOnFill(v?.color) };
  return out;
}

/** Haversine distance between two lat/lon points in km. */
export function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Format distance: "0.3 km" or "12 km". */
export function formatDistance(km) {
  return km < 10 ? km.toFixed(1) + " km" : Math.round(km) + " km";
}

/**
 * Format a points/score (or count) for compact, readable display.
 * - below 10,000: full value with locale thousands grouping ("8 450" / "8,450")
 *   so every climb still visibly moves the number;
 * - 10,000 and up: compact "k"/"M" suffix ("12,5k" / "1.2M") — at that scale
 *   (e.g. cross-gym lifetime totals) per-climb precision no longer matters and
 *   raw digit strings become unreadable.
 * The decimal/grouping separator follows the locale; the k/M suffix is kept
 * universal (HU's native compact "E"/ezer reads oddly for a score).
 *
 * `tight` = true drops the thousands grouping below 10k (returns the raw
 * integer) for width-constrained contexts like the SVG route marker badge,
 * where an added separator ("1 220p") would overflow the tiny circle. The
 * compact k/M branch is unchanged — it's already the shortest form.
 */
export function formatPoints(n, lang, tight = false) {
  const v = Math.round(Number(n) || 0);
  const locale = resolveLocale(lang);
  const abs = Math.abs(v);
  if (abs < 10000) return tight ? String(v) : v.toLocaleString(locale);
  if (abs < 1000000) return (v / 1000).toLocaleString(locale, { maximumFractionDigits: 1 }) + "k";
  return (v / 1000000).toLocaleString(locale, { maximumFractionDigits: 1 }) + "M";
}

/**
 * Format a percentage value (e.g. a billing discount) for display.
 * Follows the locale decimal separator — HU comma, EN period — and drops
 * trailing zeros: 10.2 → "10,2" (hu) / "10.2" (en), 10 → "10", 13.33 → "13,33".
 * Rounded to 2 decimals. The "%" sign is NOT appended — callers add it where
 * needed. Keeps fractional discounts readable in the operator's own locale
 * instead of hard-coding a period.
 */
export function formatPercent(n, lang) {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  const locale = resolveLocale(lang);
  return v.toLocaleString(locale, { maximumFractionDigits: 2 });
}

/**
 * The current in-app location as a relative URL (path + query + hash).
 * Used as the `from` router state when opening a full-page view (the global
 * profile) so its back control can return to the exact view — including the
 * gym and the tab — the user left, rather than a hard-coded landing page.
 */
export function currentHref() {
  try { return window.location.pathname + window.location.search + window.location.hash; }
  catch { return "/"; }
}

/**
 * Build a navigation URL that opens the platform's default map app.
 * iOS → Apple Maps, Android → default map app (geo: URI), Desktop → Google Maps.
 */
export function getNavigationUrl(address, location) {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const q = encodeURIComponent(address);
  if (isIOS) {
    return location
      ? `https://maps.apple.com/?daddr=${location.latitude},${location.longitude}&q=${q}`
      : `https://maps.apple.com/?daddr=${q}`;
  }
  if (isAndroid) {
    return location
      ? `geo:${location.latitude},${location.longitude}?q=${location.latitude},${location.longitude}`
      : `geo:0,0?q=${q}`;
  }
  // Desktop fallback: Google Maps directions
  return location
    ? `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`
    : `https://www.google.com/maps/dir/?api=1&destination=${q}`;
}

/** Forgiving match for typed danger-zone confirmation words (leave gym /
 *  delete account). Both sides are trimmed, NFD-normalized, diacritics
 *  stripped and lowercased, so "kilepes" matches "KILÉPÉS" even on a keyboard
 *  without Hungarian accents. Empty/whitespace input never matches. */
export function confirmWordMatches(input, target) {
  const norm = (s) =>
    (s || "").trim().normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const a = norm(input);
  return a.length > 0 && a === norm(target);
}

// Sunday-indexed day keys (Date.getDay() / Intl weekday → key).
const DAY_KEYS_SUN = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const pad2 = (n) => String(n).padStart(2, "0");

/** Calendar date → "YYYY-MM-DD". */
const ymd = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;

/**
 * Get current date/time info in a given IANA timezone:
 * { dayIndex (0=Sun), nowMins, year, month, day } evaluated in the zone.
 * Falls back to browser local time if timezone is null/invalid.
 */
function getTimeInZone(timezone) {
  const now = new Date();
  const local = () => ({
    dayIndex: now.getDay(), nowMins: now.getHours() * 60 + now.getMinutes(),
    year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate(),
  });
  if (!timezone) return local();
  try {
    // `en-US` on purpose, and NOT via resolveLocale: this is a machine parse.
    // The `dayMap` below keys off the short weekday names this locale emits
    // ("Sun", "Mon", …), so swapping the tag for a display preference would
    // quietly break every opening-hours comparison.
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone, weekday: "short", year: "numeric", month: "2-digit",
      day: "2-digit", hour: "numeric", minute: "numeric", hour12: false,
    }).formatToParts(now);
    const get = (type) => parts.find(p => p.type === type)?.value;
    const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    // Intl with hour12:false can emit "24" for midnight — normalize to 0.
    const hour = (parseInt(get("hour")) || 0) % 24;
    const minute = parseInt(get("minute")) || 0;
    return {
      dayIndex: dayMap[get("weekday")] ?? now.getDay(),
      nowMins: hour * 60 + minute,
      year: parseInt(get("year")) || now.getFullYear(),
      month: parseInt(get("month")) || (now.getMonth() + 1),
      day: parseInt(get("day")) || now.getDate(),
    };
  } catch {
    return local();
  }
}

/**
 * Add `offset` whole days to a calendar date.
 * Returns { dateStr: "YYYY-MM-DD", dayKey }. DST-safe (anchors at UTC noon).
 */
function addDays(year, month, day, offset) {
  const d = new Date(Date.UTC(year, month - 1, day, 12));
  d.setUTCDate(d.getUTCDate() + offset);
  return {
    dateStr: ymd(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()),
    dayKey: DAY_KEYS_SUN[d.getUTCDay()],
  };
}

/**
 * Resolve the effective opening hours for one calendar date. A matching
 * `specialHours` entry (by `date`, "YYYY-MM-DD") overrides the weekly
 * schedule: `closed:true` (or missing open/close) → closed, otherwise the
 * entry's own `{ open, close }`.
 * Returns { hours: {open,close}|null, special: boolean, label: string|null }
 * where `hours === null` means closed that day.
 */
export function getEffectiveHours(openingHours, specialHours, dateStr, dayKey) {
  const ex = Array.isArray(specialHours)
    ? specialHours.find(s => s && s.date === dateStr)
    : null;
  if (ex) {
    if (ex.closed || !ex.open || !ex.close) {
      return { hours: null, special: true, label: ex.label || null };
    }
    return { hours: { open: ex.open, close: ex.close }, special: true, label: ex.label || null };
  }
  const weekly = openingHours?.[dayKey];
  if (weekly && weekly.open && weekly.close) {
    return { hours: { open: weekly.open, close: weekly.close }, special: false, label: null };
  }
  return { hours: null, special: false, label: null };
}

/**
 * True when EVERY weekday is explicitly configured: present on the
 * openingHours object as either `null` (deliberately closed) or an object
 * with both an `open` and `close` time. A day whose key is absent — or an
 * object missing a time — counts as NOT filled in. Shared by the
 * onboarding "hours" step and the editor's inline completeness warning so
 * the two never disagree.
 */
export function isWeeklyHoursComplete(openingHours) {
  if (!openingHours || typeof openingHours !== "object") return false;
  return ["mon", "tue", "wed", "thu", "fri", "sat", "sun"].every(d => {
    if (!(d in openingHours)) return false;
    const v = openingHours[d];
    if (v === null) return true;
    return !!(v && v.open && v.close);
  });
}

/** Per-day inverse of isWeeklyHoursComplete: a day that was never set or
 *  has a blank open/close time (null = deliberately closed is NOT unset). */
export function isDayHoursUnset(openingHours, dayKey) {
  if (!openingHours || typeof openingHours !== "object" || !(dayKey in openingHours)) return true;
  const v = openingHours[dayKey];
  if (v === null) return false;
  return !(v && v.open && v.close);
}

/** True when at least one weekday has valid open+close times. A gym that
 *  is closed all seven days is not a real schedule. */
export function hasAnyOpenDay(openingHours) {
  if (!openingHours || typeof openingHours !== "object") return false;
  return ["mon", "tue", "wed", "thu", "fri", "sat", "sun"].some(d => {
    const v = openingHours[d];
    return !!(v && v.open && v.close);
  });
}

/** The weekly schedule is acceptable only when every day is explicitly
 *  configured AND the gym is open on at least one day. Used by the
 *  onboarding "hours" step; the editor reuses the two parts to show the
 *  matching warning. */
export function isWeeklyHoursValid(openingHours) {
  return isWeeklyHoursComplete(openingHours) && hasAnyOpenDay(openingHours);
}

/** Check if a gym is currently open based on its openingHours + specialHours.
 *  Evaluates in the gym's timezone if provided. specialHours overrides apply
 *  to the matching calendar date.
 *  Returns: null (no hours), false (closed), "closing" (open but ≤90 min to close), true (open).
 */
export function isGymOpen(openingHours, timezone, specialHours) {
  const hasSpecial = Array.isArray(specialHours) && specialHours.length > 0;
  if (!openingHours && !hasSpecial) return null;
  const { dayIndex, nowMins, year, month, day } = getTimeInZone(timezone);
  const { hours } = getEffectiveHours(
    openingHours, specialHours, ymd(year, month, day), DAY_KEYS_SUN[dayIndex]
  );
  if (!hours || !hours.open || !hours.close) return false;
  const [oh, om] = hours.open.split(":").map(Number);
  const [ch, cm] = hours.close.split(":").map(Number);
  const closeMins = ch * 60 + cm;
  if (nowMins < oh * 60 + om || nowMins >= closeMins) return false;
  if (closeMins - nowMins <= 90) return "closing";
  return true;
}

/**
 * Get the next opening time for a gym that is currently closed.
 * Evaluates in the gym's timezone if provided, applying specialHours.
 * Returns { day: "mon"|..., open: "HH:MM", isToday } or null if none in the next week.
 */
export function getNextOpenTime(openingHours, timezone, specialHours) {
  const hasSpecial = Array.isArray(specialHours) && specialHours.length > 0;
  if (!openingHours && !hasSpecial) return null;
  const { nowMins, year, month, day } = getTimeInZone(timezone);

  for (let offset = 0; offset < 8; offset++) {
    const { dateStr, dayKey } = addDays(year, month, day, offset);
    const { hours } = getEffectiveHours(openingHours, specialHours, dateStr, dayKey);
    if (!hours || !hours.open) continue;
    const [oh, om] = hours.open.split(":").map(Number);
    const openMins = oh * 60 + om;
    // Same day: only if opening time is still ahead
    if (offset === 0 && openMins <= nowMins) continue;
    return { day: dayKey, open: hours.open, isToday: offset === 0 };
  }
  return null;
}

/**
 * Build a rolling 7-day schedule starting today, evaluated in the gym's
 * timezone, with specialHours overrides applied. Returns 7 entries:
 *   { dateStr, dayKey, isToday, hours: {open,close}|null, special, label }
 */
export function getWeekSchedule(openingHours, specialHours, timezone) {
  const { year, month, day } = getTimeInZone(timezone);
  const out = [];
  for (let offset = 0; offset < 7; offset++) {
    const { dateStr, dayKey } = addDays(year, month, day, offset);
    const eff = getEffectiveHours(openingHours, specialHours, dateStr, dayKey);
    out.push({
      dateStr, dayKey, isToday: offset === 0,
      hours: eff.hours, special: eff.special, label: eff.label,
    });
  }
  return out;
}

/**
 * Detect a gym subdomain and return redirect info.
 * e.g. gi-boulder.app.sendly.hu → { slug: "gi-boulder", baseDomain: "app.sendly.hu" }
 * Returns null if no subdomain detected (localhost, IP, Firebase domains, reserved, etc.)
 */
export function getSubdomainRedirect() {
  const h = window.location.hostname;
  if (!h || h === "localhost" || /^[\d.:]+$/.test(h)) return null;
  if (h.endsWith(".web.app") || h.endsWith(".firebaseapp.com")) return null;
  const parts = h.split(".");
  if (parts.length < 3) return null;
  const sub = parts[0];
  if (!/^[a-z0-9][a-z0-9-]*$/.test(sub)) return null;
  const reserved = ["www", "app", "api", "admin", "mail", "ftp", "staging", "dev", "test"];
  if (reserved.includes(sub)) return null;
  return { slug: sub, baseDomain: parts.slice(1).join(".") };
}

/** Check if a feature is explicitly enabled for a gym. Missing = false. */
export function isFeatureEnabled(gymMeta, feature) {
  return !!gymMeta?.features?.[feature];
}

/** Generate a short URL-safe token for TV Dashboard access. */
export function generateTvToken() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const array = new Uint8Array(6);
  crypto.getRandomValues(array);
  return Array.from(array, b => chars[b % chars.length]).join("");
}

/** Get numeric plan limits for a gym (maxMaps, maxRoutes). */
export function getPlanLimits(gymMeta) {
  const plan = gymMeta?.plan || "free";
  const defaults = PLAN_FEATURES[plan] || PLAN_FEATURES.free;
  return {
    maxMaps: gymMeta?.features?.maxMaps ?? defaults.maxMaps,
    maxRoutes: gymMeta?.features?.maxRoutes ?? defaults.maxRoutes,
  };
}

// Random funny name generators (language-aware): an adjective and a noun from
// two comma-separated i18n lists. The keys stay literal so the i18n orphan gate sees their reader.
const NAME_CACHE = {};
function pickName(lang, adjectivesKey, nounsKey) {
  const dict = lang === "en" ? en : hu;
  const cacheKey = (lang === "en" ? "en" : "hu") + "|" + adjectivesKey;
  if (!NAME_CACHE[cacheKey]) {
    NAME_CACHE[cacheKey] = { adjectives: dict[adjectivesKey].split(","), nouns: dict[nounsKey].split(",") };
  }
  const { adjectives, nouns } = NAME_CACHE[cacheKey];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  return adj + " " + noun;
}

export function generateBoulderName(lang) {
  return pickName(lang, "names.adjectives", "names.nouns");
}

/** A random spray route name ("Hajnali Perem") — the problem editor's dice button next to the name field. */
export function generateSprayProblemName(lang) {
  return pickName(lang, "names.route.adjectives", "names.route.nouns");
}
